import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import './AttendanceReport.scss';
import { deleteAttendanceRecord } from 'store/Attendance';
import { useAppDispatch, useAppSelector } from 'store';

// Type definitions for new API response
interface AttendanceRowData {
  [key: string]: string;
  Leaves: string;
}

interface AttendanceRecord {
  id: string;
  employeeName: string;
  projectName: string;
  monthTotal: number;
  rowData: AttendanceRowData;
}

interface AttendanceEmployee {
  project: string;
  employeeName: string;
  attendance: number;
  leave: string;
}

interface ProjectGroup {
  name: string;
  employees: AttendanceEmployee[];
}

interface GroupAverage {
  project: string;
  avgPercentage: string;
}

const AttendanceReport = ({
  monthName,
  year,
  holidays,
  workingDays,
  attendanceRecords
}: {
  monthName: string;
  year: number;
  holidays: string;
  workingDays: number;
  attendanceRecords: AttendanceRecord[];
}) => {
  const { attendanceData } = useAppSelector((state) => state.attendance);
  const dispatch = useAppDispatch();

  const reportRef = useRef<HTMLDivElement>(null);
  // PDF Export handler
  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    // Dynamically load html2pdf.js if not present
    if (!(window as any).html2pdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src =
          'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
    const html2pdf = (window as any).html2pdf;
    html2pdf()
      .set({
        margin: 0.5,
        filename: `AttendanceReport_${monthName}_${year}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      })
      .from(reportRef.current)
      .save();
  };
  const [groupAverages, setGroupAverages] = useState<GroupAverage[]>([]);
  const [projects, setProjects] = useState<ProjectGroup[]>([]);
  const [totalEmployees, setTotalEmployees] = useState<number>(0);
  const [overallCompliancePercentage, setOverallCompliancePercentage] =
    useState<number>(0);
  const [totalNonCompliant, setTotalNonCompliant] = useState<number>(0);
  const [nonCompliantEmployees, setNonCompliantEmployees] = useState<
    AttendanceEmployee[]
  >([]);
  const [compliantCount, setCompliantCount] = useState<number>(0);
  const [nonCompliantCount, setNonCompliantCount] = useState<number>(0);

  useEffect(() => {
    const parseData = () => {
      try {
        const rows = attendanceRecords;
        // Filter out Thuru and Thushara
        const filteredRows = rows.filter(
          (row) =>
            row.employeeName !== 'Thuru' && row.employeeName !== 'Thushara'
        );

        // Calculate attendance and leave for each employee
        const attendanceData: AttendanceEmployee[] = filteredRows.map((row) => {
          const attendance = Number(row.monthTotal) || 0;
          const leave = row.rowData.Leaves || '';
          return {
            project: row.projectName || 'Unassigned',
            employeeName: row.employeeName,
            attendance,
            leave
          };
        });

        // Group by project
        const projectsData: { [project: string]: AttendanceEmployee[] } =
          attendanceData.reduce((acc, curr) => {
            if (!acc[curr.project]) {
              acc[curr.project] = [];
            }
            acc[curr.project].push(curr);
            return acc;
          }, {} as { [project: string]: AttendanceEmployee[] });

        // Calculate group averages and compliance
        const groupAveragesData: GroupAverage[] = Object.keys(projectsData).map(
          (project) => {
            const employees = projectsData[project];
            const totalAttendance = employees.reduce(
              (acc: number, curr: AttendanceEmployee) => acc + curr.attendance,
              0
            );
            const averageAttendance = totalAttendance / employees.length;
            const complianceRate = (averageAttendance / workingDays) * 100;
            return {
              project,
              avgPercentage: complianceRate.toFixed(2)
            };
          }
        );

        // Non-compliant employees (attendance < 8)
        const nonCompliantEmployeesData: AttendanceEmployee[] =
          attendanceData.filter((employee) => employee.attendance < 8);

        // Compliant/non-compliant counts for chart
        const compliant = attendanceData.filter(
          (emp) => emp.attendance >= 8
        ).length;
        const nonCompliant = attendanceData.length - compliant;

        setGroupAverages(groupAveragesData);
        setProjects(
          Object.keys(projectsData).map((project) => ({
            name: project,
            employees: projectsData[project]
          }))
        );
        setTotalEmployees(attendanceData.length);
        setOverallCompliancePercentage(
          Number(
            (
              (attendanceData.reduce((acc, curr) => acc + curr.attendance, 0) /
                (attendanceData.length * workingDays)) *
              100
            ).toFixed(0)
          )
        );
        setTotalNonCompliant(nonCompliantEmployeesData.length);
        setNonCompliantEmployees(nonCompliantEmployeesData);
        setCompliantCount(compliant);
        setNonCompliantCount(nonCompliant);
      } catch (error) {
        console.error(error);
      }
    };
    parseData();
  }, [monthName, year, holidays, workingDays, attendanceRecords]);

  function handleDeleteRecord() {
    if (!attendanceData?.id) return;
    dispatch(deleteAttendanceRecord(attendanceData?.id));
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
          gap: 8
        }}
      >
        <button
          type="button"
          onClick={handleDeleteRecord}
          style={{
            padding: '8px 16px',
            background: '#e6244d',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)'
          }}
        >
          Delete Record
        </button>
        <button
          type="button"
          onClick={handleExportPDF}
          style={{
            padding: '8px 16px',
            background: '#2b2b57',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)'
          }}
        >
          Download PDF
        </button>
      </div>
      <div className="attendance-bg" ref={reportRef}>
        <style>
          {`
            @media print {
              .attendance-grid {
                // display: block !important;
                grid-template-columns: 1fr !important;
              }
              .page-break-avoid, .attendance-card, .attendance-table, .projects-attendance, .attendance-row {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .attendance-card {
                page-break-after: auto !important;
              }
              body {
                margin: 0.5in;
              }
              h2, h3 {
                page-break-after: avoid;
              }
            }
          `}
        </style>
        <div className="attendance-header page-break-avoid">
          <h1 className="attendance-title">
            Employee Office Attendance Report – {monthName} {year}
          </h1>
          <p className="attendance-meta">
            Non Working Days: {holidays}
            <br />
            Working Days: {workingDays}
          </p>
        </div>
        <div className="attendance-grid page-break-avoid">
          <div className="attendance-card">
            <h2 className="attendance-title-lg">
              Group-Wise Average Attendance %
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={groupAverages}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                barCategoryGap={20}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="project"
                  tick={{ fontWeight: 600, fill: '#374151' }}
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(value: number) => `${value}%`}
                  tick={{ fontWeight: 600, fill: '#374151' }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#f9fafb',
                    borderRadius: 8,
                    border: '1px solid #d1d5db'
                  }}
                  formatter={(value: number) => `${value}%`}
                />
                <Bar
                  dataKey="avgPercentage"
                  fill="url(#barGradient)"
                  radius={[8, 8, 0, 0]}
                />
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2b2b57" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#2b2b57" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="attendance-card">
            <h2 className="attendance-title-lg">
              Attendance Compliance Overview
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { status: 'Compliant', value: compliantCount },
                    { status: 'Non-compliant', value: nonCompliantCount }
                  ]}
                  dataKey="value"
                  nameKey="status"
                  outerRadius={100}
                  label={(props: any) => {
                    const { status, percent } = props;
                    return `${status} ${(percent * 100).toFixed(0)}%`;
                  }}
                  stroke="#fff"
                >
                  <Cell fill="#10B981" />
                  <Cell fill="#EF4444" />
                </Pie>
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontWeight: 600 }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#f9fafb',
                    borderRadius: 8,
                    border: '1px solid #d1d5db'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="attendance-row page-break-avoid">
          <div className="projects-attendance">
            {projects.map((project) => (
              <div
                key={project.name}
                className={`attendance-card transition-all duration-200 hover:shadow-lg ${
                  project.employees.reduce(
                    (acc, curr) => acc + (curr.attendance / workingDays) * 100,
                    0
                  ) /
                    project.employees.length >=
                  75
                    ? 'attendance-border-success'
                    : 'attendance-border-default'
                } page-break-avoid`}
              >
                <h3 className="text-xl font-semibold mb-2 text-blue-700">
                  {project.name}
                </h3>
                <table className="attendance-table page-break-avoid">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Attendance</th>
                      <th>%</th>
                      <th>Leave Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.employees.map((employee, idx) => (
                      <tr
                        key={employee.employeeName}
                        className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                      >
                        <td className="font-medium text-gray-800">
                          <Link
                            to={`/employee/${encodeURIComponent(
                              employee.employeeName
                            )}`}
                            className="attendance-link"
                          >
                            {employee.employeeName}
                          </Link>
                        </td>
                        <td className="text-right">
                          {employee.attendance.toFixed(1)}
                        </td>
                        <td className="text-right font-semibold text-blue-600">
                          {Math.min(
                            100,
                            (employee.attendance / 8) * 100
                          ).toFixed(0)}
                          %
                        </td>
                        <td className="text-right">{employee.leave}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          <div className="lg:w-1/3 space-y-6">
            <div className="attendance-summary page-break-avoid">
              <h3 className="attendance-summary-title">Summary</h3>
              <ul className="attendance-summary-list">
                <li>
                  <span className="font-semibold">Total Employees:</span>{' '}
                  {totalEmployees}
                </li>
                <li>
                  <span className="font-semibold">Overall Compliance:</span>{' '}
                  {overallCompliancePercentage}%
                </li>
                <li>
                  <span className="font-semibold">Total Non-compliant:</span>{' '}
                  {totalNonCompliant}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="attendance-noncompliant page-break-avoid mt-4">
          <h2 className="attendance-noncompliant-title">
            Non-Compliant Employees
          </h2>
          <table className="attendance-table attendance-noncompliant-table page-break-avoid">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Project</th>
                <th>Attendance</th>
                <th>%</th>
                <th>Leaves</th>
              </tr>
            </thead>
            <tbody>
              {nonCompliantEmployees.map((employee, idx) => (
                <tr
                  key={employee.employeeName}
                  className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                >
                  <td className="font-medium text-gray-800">
                    {employee.employeeName}
                  </td>
                  <td>{employee.project}</td>
                  <td className="text-right">
                    {employee.attendance.toFixed(1)}
                  </td>
                  <td className="text-right font-semibold text-red-600">
                    {Math.min(100, (employee.attendance / 8) * 100).toFixed(0)}%
                  </td>
                  <td className="text-right">{employee.leave}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;
